import { groupRxImages, pickLatestDigitalRx } from '../../utils/rxEntry';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { useScope, usePermissions } from '../../context/PermissionContext';
import { VISIT_ACTIONS, META_TYPES } from '../../constants/visitMetadata';
import { deleteMetadata, downloadFileSecure } from '../../services/api/visitMetadataApi';
import { openPrescriptionPdf } from '../../services/api/prescriptionPdfApi';
import useFileViewerStore from '../../stores/useFileViewerStore';
import eventBus from '../../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../../services/realtime/RealtimeSyncService';
import { useVisitMetadata } from '../../hooks/useVisitMetadata';
import { listVisitNotes } from '../../services/api/procedureNoteApi';
import syncEngine from '../../services/SyncEngine';
import AddNotes from './AddNotes';
import AddVitals from './AddVitals';
import AddSymptoms from './AddSymptoms';
import AddPrescription from './AddPrescription'; // Used for report & prescription uploads
import DigitalPrescriptionForm from './DigitalPrescriptionForm';
import PrescriptionReadOnlyView from './PrescriptionReadOnlyView';
import BillingCard from './BillingCard';
import VisitProceduresPanel from './VisitProceduresPanel';
import LabAdvisePanel from './LabAdvisePanel';
import LabAssignPanel from './LabAssignPanel';
import VisitContextHeader from './VisitContextHeader';
import ActionPendingControls from './ActionPendingControls';
import FollowupBanner from './FollowupBanner';
import { formatDoseSchedule } from '../../utils/rxDosage';
import CoWorkerAvatarsRow from './CoWorkerAvatarsRow';
import CoWorkerPickerDialog from './CoWorkerPickerDialog';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';
import useLabEnabled from '../../hooks/useLabEnabled';
import { hasPrescriptionContent } from '../../utils/prescriptionContent';

/**
 * VisitActionsPanel — Slide-down panel attached to each visit card.
 *
 * Shows:
 *  1. Action buttons (Notes, Vitals, Symptoms, Prescription*, Report*)
 *  2. Active form when an action is clicked
 *  3. Existing metadata entries (permission-filtered by backend)
 *
 * Permission:
 *  - Each action filtered by central ROLE_PERMISSIONS via hasPermission()
 *  - Backend enforces write permissions
 *  - Backend filters read visibility (VIEW_ALL_METADATA permission)
 *
 * SYNC FIX: Uses useVisitMetadata hook → SyncEngine for:
 *  - fetchOncePerVisit guard (prevents repeated metadata API calls)
 *  - Request deduplication (multiple components fetching same visit)
 *  - AbortController on unmount (no ghost API calls)
 *  - WS-driven refresh (auto-invalidates cache on VISIT_METADATA_ADDED)
 *  - Stable callback identity (prevents re-render → fetch → render loops)
 *
 * R5 (owner, final ruling) — `readOnly` (default false): every write
 * surface on every tab the viewer gets is suppressed — no Add/Save/Assign/
 * Advise/Collect control, no delete, no write call of any kind — while
 * existing entries/values still show. The gate is GENERIC per card (each
 * card is handed `readOnly`/`reportOnly`), never a hard-coded tab list, so
 * a per-member permission override that grants an extra tab (e.g.
 * ADD_VITALS) inherits the same read-only treatment automatically. Set
 * ONLY by ManageVisitPage when the viewer is a RECEPTIONIST — every OTHER
 * mount (ReceptionistDashboard, NurseTaskCards, DoctorQueueView,
 * AdminSummary) never passes it, so they stay byte-identical to before.
 * A completed visit not being editable from Manage Visits by the desk is
 * the owner's intended behaviour, not a gap.
 */
// PO-ratified — one line, so a wording change never touches JSX.
const DETAILS_READ_ONLY_HINT = 'View only here — make changes from your Receptionist Dashboard instead.';

export default function VisitActionsPanel({ visit, userRole, onClose, onMetadataLoaded, initialTab, hideActionBar, readOnly = false }) {
  const { isDark } = useTheme();
  const { memberId } = useScope();
  const { hasPermission } = usePermissions();
  const role = (userRole || '').toUpperCase();
  const [isMaximized, setIsMaximized] = useState(false);
  const isDoctor = role === 'DOCTOR';
  const labEnabled = useLabEnabled();
  // Doctors default to "prescription" tab open; others default to no tab
  const [activeAction, setActiveAction] = useState(initialTab || (isDoctor ? 'prescription' : null));

  // Phase 3.0 — the half-disc procedure widget is mounted once at the
  // page level by DoctorQueueView. VisitActionsPanel keeps its legacy
  // Procedures tab hidden for doctors so the disc owns add/edit/delete
  // and there's no duplicate surface.
  const showProcedureWidget = isDoctor && !!visit?.visit_id;

  // Sync with external initialTab prop changes
  // FIX: Only fire when initialTab actually changes value, not identity
  const prevInitialTabRef = useRef(initialTab);
  useEffect(() => {
    if (initialTab !== undefined && initialTab !== prevInitialTabRef.current) {
      prevInitialTabRef.current = initialTab;
      setActiveAction(initialTab);
    }
  }, [initialTab]);

  const [successMsg, setSuccessMsg] = useState(null);
  // Phase 6.0 / 10 — co-worker picker dialog state.
  const [coWorkerPickerOpen, setCoWorkerPickerOpen] = useState(false);

  // ── SYNC FIX: Use centralized metadata hook instead of raw fetch ──
  // This replaces the old pattern that had unstable callback dependencies:
  //   loadMetadata depended on onMetadataLoaded (inline arrow from parent)
  //   → new identity each render → useEffect re-fired → fetch → re-render
  const { metadata, loading: loadingMeta, refresh: refreshMetadata } = useVisitMetadata(visit.visit_id);

  // FIX: Notify parent of metadata count via ref-stabilized callback
  // This breaks the render loop: onMetadataLoaded no longer in any dep array
  const onMetadataLoadedRef = useRef(onMetadataLoaded);
  onMetadataLoadedRef.current = onMetadataLoaded;
  useEffect(() => {
    onMetadataLoadedRef.current?.(metadata.length);
  }, [metadata.length]); // Only fires when count actually changes

  // Workflow logging
  const { logAction: logWorkflow } = useWorkflowStore();

  /* ── Maximize: lock body scroll + Escape key ── */
  useEffect(() => {
    if (!isMaximized) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setIsMaximized(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [isMaximized]);

  // Determine which doctor specialization applies to this visit
  const doctorSpecialization = visit.doctor_specialization || '';

  // Filter actions by user permission (central ROLE_PERMISSIONS)
  // (role and isDoctor already declared above for default tab logic)

  // For DOCTOR role: Notes, Vitals, Symptoms are merged into the unified
  // Doctor Workspace (Prescription tab), so hide them from the action bar.
  const DOCTOR_HIDDEN_TABS = new Set(['notes', 'vitals', 'symptoms', 'report']);
  const availableActions = VISIT_ACTIONS.filter(
    (action) => {
      if (!action.permission ? !action.roles.includes(role) : !hasPermission(action.permission)) return false;
      if (isDoctor && DOCTOR_HIDDEN_TABS.has(action.key)) return false;
      // Phase 3.0 — when the half-disc widget is active for this user,
      // the legacy Procedures tab in the action bar is redundant: the
      // disc owns add / edit / delete / collect end-to-end. Hiding it
      // prevents two competing surfaces from confusing the doctor.
      if (showProcedureWidget && action.key === 'procedures') return false;
      return true;
    }
  );

  // Handle successful add — keep form open, refresh entries, emit local event, log workflow
  const handleSuccess = useCallback(
    (msg) => {
      setSuccessMsg(msg);
      // DON'T close the form — let user add more
      // FIX: Invalidate cache and refresh via SyncEngine (not raw fetch)
      syncEngine.invalidateMetadata(visit.visit_id);
      refreshMetadata();

      // Log workflow action based on active action type
      const actionMap = {
        notes: WORKFLOW_ACTIONS.ADD_NOTES,
        vitals: WORKFLOW_ACTIONS.ADD_VITALS,
        symptoms: WORKFLOW_ACTIONS.ADD_SYMPTOMS,
        prescription: WORKFLOW_ACTIONS.ADD_PRESCRIPTION,
        report: WORKFLOW_ACTIONS.ADD_REPORT,
      };
      const workflowAction = actionMap[activeAction];
      if (workflowAction) {
        logWorkflow(visit.visit_id, workflowAction);
      }

      // Emit local event so notification hooks on OTHER panels can track it
      eventBus.emit({
        type: REALTIME_EVENTS.VISIT_METADATA_ADDED,
        payload: { visitId: visit.visit_id, entry: { added_by_role: role } },
        source: 'local',
        timestamp: Date.now(),
      });
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    [refreshMetadata, visit.visit_id, role, activeAction, logWorkflow]
  );

  // Handle delete — R5: no write call of any kind under readOnly.
  const handleDelete = useCallback(
    async (entry) => {
      if (readOnly) return;
      try {
        await deleteMetadata(visit.visit_id, entry.meta_type, entry.meta_id);
        // FIX: Use SyncEngine cache invalidation + refresh
        syncEngine.invalidateMetadata(visit.visit_id);
        refreshMetadata();
      } catch (err) {
        console.error('Delete failed:', err);
      }
    },
    [visit.visit_id, refreshMetadata, readOnly]
  );
  // R5 — no delete AFFORDANCE (not just no-op handler) anywhere under
  // readOnly: MetadataSection only renders the 🗑 button when `onDelete`
  // is truthy, so handing it `undefined` removes the control itself.
  const deleteHandler = readOnly ? undefined : handleDelete;

  // Phase 4.0i — visit-level Notes section now merges:
  //   1. Generic visit NOTE metadata (typed-by-doctor, free-form).
  //   2. Procedure-bound notes from the new ProcedureNote thread, fetched
  //      via the visit-axis aggregation endpoint and live-updated via
  //      SITTING_NOTE_ADDED. Adapter rows mimic the metadata shape so
  //      MetadataSection can render them without a separate code path.
  const [procedureNotes, setProcedureNotes] = useState([]);
  useEffect(() => {
    if (!visit?.visit_id) return;
    let cancelled = false;
    (async () => {
      // try/catch, not a bare await: this IIFE is fire-and-forget, so a
      // rejected fetch (5xx / offline) escaped as an UNHANDLED rejection.
      // Procedure notes are one section of this panel — failing to load them
      // must not take the whole visit panel down. Same shape as the sibling
      // lanes in PatientHistory / ManageVisitPage.
      try {
        const r = await listVisitNotes(visit.visit_id);
        if (cancelled) return;
        if (r?.success) setProcedureNotes(r.data?.notes || []);
      } catch (err) {
        console.error('Failed to load procedure notes for visit:', visit.visit_id, err);
      }
    })();
    return () => { cancelled = true; };
  }, [visit?.visit_id]);

  // Live append: SITTING_NOTE_ADDED carries the full note payload. We
  // upsert by note_id so dedupe is automatic when the WS event races the
  // initial fetch. visit_id filter keeps notes from other open visits
  // out of this panel.
  useEffect(() => {
    if (!visit?.visit_id) return;
    const off = eventBus.on(REALTIME_EVENTS.SITTING_NOTE_ADDED, (event) => {
      const note = event?.payload;
      if (!note || note.visit_id !== visit.visit_id) return;
      setProcedureNotes((prev) => {
        if (prev.some((n) => n.note_id === note.note_id)) return prev;
        return [...prev, note].sort(
          (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
        );
      });
    });
    return off;
  }, [visit?.visit_id]);

  const procedureNoteRows = procedureNotes.map((n) => ({
    meta_id: n.note_id,
    meta_type: META_TYPES.PROCEDURE_NOTE,
    content: n.body,
    added_by_member_id: n.author?.member_id,
    added_by_name: n.author?.name || 'Unknown',
    added_by_role: n.author?.role || '',
    created_at: n.created_at,
    sitting_id: n.sitting_id,
    is_procedure_note: true,
  }));

  const noteEntries = [
    ...metadata.filter((e) => e.meta_type === META_TYPES.NOTE),
    ...procedureNoteRows,
  ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const vitalEntries = metadata.filter((e) => e.meta_type === META_TYPES.VITAL);
  const symptomEntries = metadata.filter((e) => e.meta_type === META_TYPES.SYMPTOM);
  // Attached images live as separate PRESCRIPTION_IMAGE rows, linked back to
  // their parent Rx via prescription_meta_id (set at upload time) — group
  // them onto each prescription entry here so read-only/edit views can show
  // them without each needing its own metadata fetch + linkage logic.
  const prescriptionEntries = groupRxImages(metadata);
  const reportEntries = metadata.filter((e) => e.meta_type === META_TYPES.REPORT);
  const hasEntries = metadata.length > 0;

  // Open file securely (streamed through backend, no presigned URL)
  const handleViewFile = useCallback(async (entry) => {
    try {
      const { blobUrl, contentType } = await downloadFileSecure(visit.visit_id, entry.meta_type, entry.meta_id);
      const ft = contentType?.startsWith('image') ? 'image' : 'pdf';
      useFileViewerStore.getState().openViewer({
        blobUrl,
        fileType: ft,
        title: entry.meta_type === 'PRESCRIPTION' ? 'Prescription' : entry.meta_type === 'REPORT' ? 'Report' : 'File',
      });
    } catch (err) {
      console.error('Failed to view file:', err);
    }
  }, [visit.visit_id]);

  return (
    <div
      style={{
        ...(isMaximized
          ? {
              position: 'fixed', inset: 0, zIndex: 1100,
              background: isDark ? '#111827' : '#ffffff',
              overflowY: 'auto',
              display: 'flex', flexDirection: 'column',
            }
          : {
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              background: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.005)',
              animation: 'slideDown 0.2s ease',
              display: 'flex', flexDirection: 'column',
            }),
      }}
    >
      {/* Phase 1c — patient + visit + team panel, visible to all roles. */}
      <VisitContextHeader
        visit={visit}
        viewerMemberId={memberId}
        isDark={isDark}
      />

      {/* Phase 6.0 — visit-level action-pending overlay + co-worker
          row. Renders chips, role-aware Resolve / Forward buttons,
          follow-up banner, and the assigned-doctor + co-worker pills.
          Origination of new hops lives in SendVisitDialog. Doesn't
          gate any existing action — purely additive. */}
      <div style={{ padding: '0.5rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <FollowupBanner visit={visit} />
        <ActionPendingControls
          visit={visit}
          viewer={{ memberId, role }}
          onVisitChanged={(updatedVisit) => {
            if (updatedVisit) {
              setSuccessMsg('Action updated');
              setTimeout(() => setSuccessMsg(null), 2500);
            }
          }}
        />
        <CoWorkerAvatarsRow
          visit={visit}
          viewer={{ memberId, role }}
          onAddClick={() => setCoWorkerPickerOpen(true)}
        />
      </div>

      {coWorkerPickerOpen && (
        <CoWorkerPickerDialog
          visit={visit}
          onSubmitted={() => {
            setCoWorkerPickerOpen(false);
            setSuccessMsg('Co-worker added');
            setTimeout(() => setSuccessMsg(null), 2500);
          }}
          onCancel={() => setCoWorkerPickerOpen(false)}
        />
      )}

      {/* Success message */}
      {successMsg && (
        <div style={{
          padding: '0.5rem 1rem',
          background: isDark ? 'rgba(34,197,94,0.1)' : '#f0fdf4',
          borderBottom: `1px solid ${isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7'}`,
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          fontSize: '0.75rem',
          color: isDark ? '#4ade80' : '#166534',
        }}>
          ✅ {successMsg}
        </div>
      )}

      {/* Action Buttons Bar — hidden when parent provides its own tab bar */}
      {!hideActionBar && (
      <div style={{
        display: 'flex',
        gap: '0.375rem',
        padding: '0.625rem 1rem',
        flexWrap: 'wrap',
        alignItems: 'center',
        ...(isMaximized && {
          position: 'sticky', top: 0, zIndex: 10,
          background: isDark ? '#111827' : '#ffffff',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        }),
      }}>
        <span style={{ fontSize: '0.625rem', fontWeight: 600, color: isDark ? '#6b7280' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '0.25rem' }}>
          Actions:
        </span>
        {availableActions.map((action) => {
          const isActive = activeAction === action.key;
          // Show entry count badge per action type
          const typeCountMap = { notes: noteEntries.length, vitals: vitalEntries.length, symptoms: symptomEntries.length, prescription: prescriptionEntries.length, report: reportEntries.length };
          const entryCount = typeCountMap[action.key] || 0;
          return (
            <button
              key={action.key}
              onClick={() => {
                if (action.disabled) return;
                setActiveAction(isActive ? null : action.key);
              }}
              disabled={action.disabled}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.3rem 0.625rem',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: isActive ? 600 : 400,
                border: isActive
                  ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                  : entryCount > 0
                  ? `1px solid ${isDark ? 'rgba(13, 148, 136,0.3)' : 'rgba(13, 148, 136,0.2)'}`
                  : `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)'}`,
                // RV-6 — inactive-state rule: a solid inactive fill, never
                // `transparent` (bk-994/D2). Same tab-bar convention as the
                // rest of the app (`bg-white`/`#f8fafc` light, `#1e293b` dark).
                background: isActive
                  ? isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)'
                  : isDark ? '#1e293b' : '#f8fafc',
                color: action.disabled
                  ? isDark ? '#4b5563' : '#d1d5db'
                  : isActive
                  ? '#3b82f6'
                  : isDark ? '#cbd5e1' : '#374151',
                cursor: action.disabled ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                opacity: action.disabled ? 0.5 : 1,
                position: 'relative',
              }}
            >
              <span>{action.icon}</span>
              <span>{action.label}</span>
              {entryCount > 0 && (
                <span style={{
                  fontSize: '0.5625rem',
                  fontWeight: 600,
                  padding: '0.05rem 0.3rem',
                  borderRadius: '9999px',
                  background: isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.1)',
                  color: '#0d9488',
                  minWidth: '0.875rem',
                  textAlign: 'center',
                }}>
                  {entryCount}
                </span>
              )}
              {action.comingSoon && (
                <span style={{
                  fontSize: '0.5rem',
                  padding: '0.05rem 0.25rem',
                  borderRadius: '9999px',
                  background: isDark ? 'rgba(234,179,8,0.15)' : 'rgba(234,179,8,0.1)',
                  color: '#eab308',
                  fontWeight: 600,
                }}>
                  Soon
                </span>
              )}
            </button>
          );
        })}

        {/* Billing button — RECEPTIONIST / ADMIN only */}
        {hasPermission('CREATE_VISIT') && !isDoctor && (
          <button
            onClick={() => setActiveAction(activeAction === 'billing' ? null : 'billing')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.3rem 0.625rem', borderRadius: '0.5rem',
              fontSize: '0.8125rem',
              fontWeight: activeAction === 'billing' ? 600 : 400,
              border: activeAction === 'billing'
                ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                : `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)'}`,
              background: activeAction === 'billing'
                ? isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)'
                : isDark ? '#1e293b' : '#f8fafc',
              color: activeAction === 'billing' ? '#3b82f6' : isDark ? '#cbd5e1' : '#374151',
              cursor: 'pointer', transition: 'background 0.15s, color 0.15s, border-color 0.15s',
            }}
          >
            <span>💰</span>
            <span>Billing</span>
            {(visit.consultation_fee != null) && (
              <span style={{
                fontSize: '0.5625rem', fontWeight: 600,
                padding: '0.05rem 0.3rem', borderRadius: '9999px',
                background: isDark ? 'rgba(13,148,136,0.15)' : 'rgba(13,148,136,0.1)',
                color: '#0d9488',
              }}>₹</span>
            )}
          </button>
        )}

        {/* Expand / Minimize + Close */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <button
            onClick={() => setIsMaximized(v => !v)}
            title={isMaximized ? 'Minimize (Esc)' : 'Open fullscreen'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.08)',
              border: `1px solid ${isDark ? 'rgba(13,148,136,0.25)' : 'rgba(13,148,136,0.15)'}`,
              borderRadius: '0.375rem',
              color: isDark ? '#2dd4bf' : '#0f766e',
              cursor: 'pointer',
              padding: '0.2rem 0.45rem',
              fontSize: '0.5625rem',
              fontWeight: 600,
              fontFamily: 'system-ui, sans-serif',
              lineHeight: 1,
              transition: 'all 0.15s ease',
              gap: '0.2rem',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = isDark ? 'rgba(13,148,136,0.2)' : 'rgba(13,148,136,0.15)';
              e.currentTarget.style.transform = 'scale(1.04)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.08)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isMaximized ? (
              <>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 4 10 0 10" /><polyline points="12 2 12 6 16 6" /><line x1="14" y1="0" x2="9" y2="5" /><line x1="2" y1="16" x2="7" y2="11" />
                </svg>
                Minimize
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="14 2 14 6 10 6" /><polyline points="2 14 2 10 6 10" /><line x1="14" y1="2" x2="9" y2="7" /><line x1="2" y1="14" x2="7" y2="9" />
                </svg>
                Expand
              </>
            )}
          </button>
          <button
            onClick={() => { setIsMaximized(false); onClose(); }}
            style={{
              background: isDark ? '#26262e' : '#f8fafc',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
              borderRadius: '0.375rem',
              padding: '0.2rem 0.5rem',
              cursor: 'pointer',
              fontSize: '0.75rem',
              color: isDark ? '#e8e9ed' : '#374151',
            }}
          >
            {isMaximized ? '✕ Close' : '▲ Close'}
          </button>
        </div>
      </div>
      )}

      {/* R5 — one hint, once, at the top of the panel content, chrome floor
          0.75rem+, full-contrast muted ink on a solid surface, never a
          button. Same line regardless of which tab is open. */}
      {readOnly && (
        <div style={{
          margin: '0 1rem 0.75rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.5rem',
          fontSize: '0.75rem',
          fontWeight: 500,
          color: isDark ? '#9ca3af' : '#6b7280',
          background: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        }}>
          {DETAILS_READ_ONLY_HINT}
        </div>
      )}

      {/* Active Form + related existing entries inline */}
      {activeAction === 'notes' && (
        <div style={{
          margin: '0 1rem 0.75rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        }}>
          {!readOnly && (
            <AddNotes
              visitId={visit.visit_id}
              onSuccess={handleSuccess}
              onClose={() => setActiveAction(null)}
            />
          )}
          {/* Show existing notes below the form */}
          {noteEntries.length > 0 && (
            <div style={{ padding: '0 1rem 0.75rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`, marginTop: '0.25rem', paddingTop: '0.625rem' }}>
              <MetadataSection title="📝 Previous Notes" entries={noteEntries} isDark={isDark} memberId={memberId} role={role} onDelete={deleteHandler}
                renderContent={(entry) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {entry.meta_type === META_TYPES.PROCEDURE_NOTE && (
                      <span
                        style={{
                          alignSelf: 'flex-start',
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          padding: '0.1rem 0.4rem',
                          borderRadius: 9999,
                          background: isDark ? 'rgba(13,148,136,0.20)' : '#ccfbf1',
                          color: isDark ? '#5eead4' : '#0f766e',
                        }}
                      >
                        🦷 Procedure note{entry.procedure_name ? ` · ${entry.procedure_name}` : ''}
                      </span>
                    )}
                    <p style={{ margin: 0, fontSize: '0.75rem', color: isDark ? '#e8e9ed' : '#374151', lineHeight: 1.5 }}>{entry.content}</p>
                  </div>
                )}
              />
            </div>
          )}
        </div>
      )}

      {activeAction === 'vitals' && (
        <div style={{
          margin: '0 1rem 0.75rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        }}>
          {!readOnly && (
            <AddVitals
              visitId={visit.visit_id}
              onSuccess={handleSuccess}
              onClose={() => setActiveAction(null)}
            />
          )}
          {/* Show existing vitals below the form */}
          {vitalEntries.length > 0 && (
            <div style={{ padding: '0 1rem 0.75rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`, marginTop: '0.25rem', paddingTop: '0.625rem' }}>
              <MetadataSection title="🌡️ Previous Vitals" entries={vitalEntries} isDark={isDark} memberId={memberId} role={role} onDelete={deleteHandler}
                renderContent={(entry) => (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                    {(entry.vitals || []).map((v, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.5rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                        background: v.out_of_range ? (isDark ? 'rgba(234,179,8,0.1)' : 'rgba(234,179,8,0.08)') : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                        border: v.out_of_range ? '1px solid rgba(234,179,8,0.3)' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                        color: isDark ? '#e8e9ed' : '#374151',
                      }}>
                        <span style={{ fontWeight: 500, color: isDark ? '#9ca3af' : '#6b7280' }}>{v.label}:</span>
                        <span style={{ fontWeight: 600 }}>{v.value}</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: isDark ? '#e8e9ed' : '#374151' }}>{v.unit}</span>
                        {v.out_of_range && <span style={{ color: isDark ? '#fbbf24' : '#b45309' }}>⚠</span>}
                      </span>
                    ))}
                  </div>
                )}
              />
            </div>
          )}
        </div>
      )}

      {activeAction === 'symptoms' && (
        <div style={{
          margin: '0 1rem 0.75rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        }}>
          {!readOnly && (
            <AddSymptoms
              visitId={visit.visit_id}
              doctorSpecialization={doctorSpecialization}
              onSuccess={handleSuccess}
              onClose={() => setActiveAction(null)}
            />
          )}
          {/* Show existing symptoms below the form */}
          {symptomEntries.length > 0 && (
            <div style={{ padding: '0 1rem 0.75rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`, marginTop: '0.25rem', paddingTop: '0.625rem' }}>
              <MetadataSection title="🤒 Previous Symptoms" entries={symptomEntries} isDark={isDark} memberId={memberId} role={role} onDelete={deleteHandler}
                renderContent={(entry) => (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {(entry.symptoms || []).map((s, i) => (
                      <span key={i} style={{ padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.625rem', fontWeight: 500,
                        background: s.is_custom ? (isDark ? 'rgba(13, 148, 136,0.12)' : 'rgba(13, 148, 136,0.08)') : (isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)'),
                        color: s.is_custom ? '#0d9488' : '#3b82f6',
                      }}>{s.name}</span>
                    ))}
                  </div>
                )}
              />
            </div>
          )}
        </div>
      )}

      {/* Prescription — Digital + Upload sub-tabs */}
      {activeAction === 'prescription' && (
        <PrescriptionSubPanel
          visit={visit}
          isDark={isDark}
          prescriptionEntries={prescriptionEntries}
          handleSuccess={handleSuccess}
          handleDelete={deleteHandler}
          handleViewFile={handleViewFile}
          memberId={memberId}
          role={role}
          setActiveAction={setActiveAction}
          doctorSpecialization={doctorSpecialization}
          isDoctor={isDoctor}
          loadingMeta={loadingMeta}
          readOnly={readOnly}
        />
      )}

      {/* Report Upload */}
      {activeAction === 'report' && (
        <div style={{
          margin: '0 1rem 0.75rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        }}>
          {!readOnly && (
            <AddPrescription
              visitId={visit.visit_id}
              metaType="REPORT"
              onSuccess={handleSuccess}
              onClose={() => setActiveAction(null)}
            />
          )}
          {reportEntries.length > 0 && (
            <div style={{ padding: '0 1rem 0.75rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`, marginTop: '0.25rem', paddingTop: '0.625rem' }}>
              <MetadataSection title="📄 Uploaded Reports" entries={reportEntries} isDark={isDark} memberId={memberId} role={role} onDelete={deleteHandler}
                renderContent={(entry) => (
                  <FileEntryContent entry={entry} isDark={isDark} onView={handleViewFile} />
                )}
              />
            </div>
          )}
        </div>
      )}

      {/* Procedures (Phase 1 + 2.5) — hidden when the Phase 3.0 disc widget owns this surface */}
      {activeAction === 'procedures' && !showProcedureWidget && (
        <div style={{
          margin: '0 1rem 0.75rem',
          borderRadius: '0.625rem',
          // Phase 2.5 — solid backgrounds so nested indigo/teal panels don't
          // blend into a milky overlay (the previous rgba(255,255,255,0.5)
          // made everything look washed out in day mode).
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
          background: isDark ? '#0f172a' : '#ffffff',
        }}>
          <VisitProceduresPanel
            visit={visit}
            viewer={{ memberId, role }}
            onClose={() => setActiveAction(null)}
            readOnly={readOnly}
          />
        </div>
      )}

      {activeAction === 'lab' && (
        <div style={{
          margin: '0 1rem 0.75rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
          background: isDark ? '#0f172a' : '#ffffff',
          padding: '0.75rem',
        }}>
          {/* Doctor advises (ORDER_LAB); everyone else who can see the tab
              (receptionist via ASSIGN_LAB_ORDER, technician read-only) gets the
              assign surface for this visit's advised labs.
              When the lab module is on, a doctor advises labs from the
              prescription's Tests section instead — so the advise dropdown is
              retired here and replaced with a pointer. */}
          {hasPermission('ORDER_LAB')
            ? (labEnabled && isDoctor
                ? (
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    🧪 Write tests in Prescription → Tests. They appear here and at the front desk, and reception raises the orders.
                  </div>
                )
                : <LabAdvisePanel visit={visit} readOnly={readOnly} />)
            : <LabAssignPanel visit={visit} readOnly={readOnly} />}
        </div>
      )}

      {/* Billing Card */}
      {activeAction === 'billing' && (
        <div style={{
          margin: '0 1rem 0.75rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        }}>
          <BillingCard
            visit={visit}
            readOnly={readOnly}
            onClose={() => setActiveAction(null)}
          />
        </div>
      )}

      {/* All Metadata Display — shown when no form is active */}
      {hasEntries && !activeAction && (
        <div style={{ padding: '0 1rem 0.75rem' }}>
          {/* Notes */}
          {noteEntries.length > 0 && (
            <MetadataSection
              title="📝 Notes"
              entries={noteEntries}
              isDark={isDark}
              memberId={memberId}
              role={role}
              onDelete={deleteHandler}
              renderContent={(entry) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {entry.meta_type === META_TYPES.PROCEDURE_NOTE && (
                    <span
                      style={{
                        alignSelf: 'flex-start',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        padding: '0.1rem 0.4rem',
                        borderRadius: 9999,
                        background: isDark ? 'rgba(13,148,136,0.20)' : '#ccfbf1',
                        color: isDark ? '#5eead4' : '#0f766e',
                      }}
                    >
                      🦷 Procedure note{entry.procedure_name ? ` · ${entry.procedure_name}` : ''}
                    </span>
                  )}
                  <p style={{ margin: 0, fontSize: '0.75rem', color: isDark ? '#e8e9ed' : '#374151', lineHeight: 1.5 }}>
                    {entry.content}
                  </p>
                </div>
              )}
            />
          )}

          {/* Vitals */}
          {vitalEntries.length > 0 && (
            <MetadataSection
              title="🌡️ Vitals"
              entries={vitalEntries}
              isDark={isDark}
              memberId={memberId}
              role={role}
              onDelete={deleteHandler}
              renderContent={(entry) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {(entry.vitals || []).map((v, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '0.375rem',
                        fontSize: '0.8125rem',
                        background: v.out_of_range
                          ? isDark ? 'rgba(234,179,8,0.1)' : 'rgba(234,179,8,0.08)'
                          : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        border: v.out_of_range
                          ? '1px solid rgba(234,179,8,0.3)'
                          : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                        color: isDark ? '#e8e9ed' : '#374151',
                      }}
                    >
                      <span style={{ fontWeight: 500, color: isDark ? '#9ca3af' : '#6b7280' }}>{v.label}:</span>
                      <span style={{ fontWeight: 600 }}>{v.value}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500, color: isDark ? '#e8e9ed' : '#374151' }}>{v.unit}</span>
                      {v.out_of_range && <span style={{ color: isDark ? '#fbbf24' : '#b45309' }}>⚠</span>}
                    </span>
                  ))}
                </div>
              )}
            />
          )}

          {/* Symptoms */}
          {symptomEntries.length > 0 && (
            <MetadataSection
              title="🤒 Symptoms"
              entries={symptomEntries}
              isDark={isDark}
              memberId={memberId}
              role={role}
              onDelete={deleteHandler}
              renderContent={(entry) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {(entry.symptoms || []).map((s, i) => (
                    <span
                      key={i}
                      style={{
                        padding: '0.15rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.625rem',
                        fontWeight: 500,
                        background: s.is_custom
                          ? isDark ? 'rgba(13, 148, 136,0.12)' : 'rgba(13, 148, 136,0.08)'
                          : isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)',
                        color: s.is_custom ? '#0d9488' : '#3b82f6',
                      }}
                    >
                      {s.name}
                    </span>
                  ))}
                </div>
              )}
            />
          )}

          {/* Prescriptions — delete is handled by MetadataSection header trash icon */}
          {prescriptionEntries.length > 0 && (
            <MetadataSection
              title="💊 Prescriptions"
              entries={prescriptionEntries}
              isDark={isDark}
              memberId={memberId}
              role={role}
              onDelete={visit.status !== 'COMPLETED' ? deleteHandler : null}
              renderContent={(entry) => {
                if (entry.prescription_type === 'digital' && hasPrescriptionContent(entry)) {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {(entry.medicines || []).map((med, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap',
                          fontSize: '0.6875rem', color: isDark ? '#e8e9ed' : '#374151',
                        }}>
                          <span style={{ fontWeight: 600 }}>{med.medicine}</span>
                          {med.strength && <span style={{ color: isDark ? '#2dd4bf' : '#0f766e', fontSize: '0.625rem' }}>{med.strength}</span>}
                          {med.form && <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.625rem' }}>({med.form})</span>}
                          {med.dosage_schedule && <span style={{ fontSize: '0.625rem', color: isDark ? '#60a5fa' : '#3b82f6' }}>
                            {formatDoseSchedule(med.dosage_schedule)}
                          </span>}
                          {med.timing && <span style={{ fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>{med.timing}</span>}
                          {med.duration && <span style={{ fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>{med.duration}</span>}
                        </div>
                      ))}
                      {entry.investigations && entry.investigations.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center', marginTop: '0.25rem' }}>
                          <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: isDark ? '#fbbf24' : '#d97706' }}>🧪</span>
                          {entry.investigations.map((inv, i) => (
                            <span key={i} style={{
                              fontSize: '0.5rem', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem',
                              background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)',
                              color: isDark ? '#fbbf24' : '#d97706', fontWeight: 500,
                            }}>{inv}</span>
                          ))}
                        </div>
                      )}
                      {entry.doctor_notes && (
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.625rem', fontStyle: 'italic', color: isDark ? '#9ca3af' : '#6b7280' }}>
                          📝 {entry.doctor_notes}
                        </p>
                      )}
                    </div>
                  );
                }
                return (
                  <FileEntryContent entry={entry} isDark={isDark} onView={handleViewFile} />
                );
              }}
            />
          )}

          {/* Reports */}
          {reportEntries.length > 0 && (
            <MetadataSection
              title="📄 Reports"
              entries={reportEntries}
              isDark={isDark}
              memberId={memberId}
              role={role}
              onDelete={deleteHandler}
              renderContent={(entry) => (
                <FileEntryContent entry={entry} isDark={isDark} onView={handleViewFile} />
              )}
            />
          )}
        </div>
      )}

      {/* Loading */}
      {loadingMeta && !hasEntries && (
        <div style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
          <span style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af' }}>Loading metadata...</span>
        </div>
      )}

      {/* Empty state */}
      {!loadingMeta && !hasEntries && !activeAction && (
        <div style={{
          padding: '0.75rem 1rem',
          textAlign: 'center',
          color: isDark ? '#6b7280' : '#9ca3af',
          fontSize: '0.6875rem',
        }}>
          No records yet. Click an action above to add notes, vitals, prescriptions, or more.
        </div>
      )}
    </div>
  );
}


/**
 * FileEntryContent — Renders file name + size + view button for prescription/report entries.
 */
function FileEntryContent({ entry, isDark, onView }) {
  const isImage = entry.content_type?.startsWith('image/');
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: '0.875rem', flexShrink: 0 }}>{isImage ? '🖼️' : '📄'}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '0.6875rem', fontWeight: 500,
            color: isDark ? '#e8e9ed' : '#374151',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {entry.file_name || 'File'}
          </div>
          {entry.file_size && (
            <span style={{ fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
              {formatSize(entry.file_size)}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => onView(entry)}
        style={{
          padding: '0.2rem 0.5rem',
          borderRadius: '0.375rem',
          border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.3)' : 'rgba(13, 148, 136,0.2)'}`,
          background: isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.05)',
          color: '#0d9488',
          fontSize: '0.5625rem',
          fontWeight: 600,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        👁 View
      </button>
    </div>
  );
}


/**
 * MetadataSection — Renders a group of metadata entries (notes, vitals, or symptoms).
 */
function MetadataSection({ title, entries, isDark, memberId, role, onDelete, renderContent }) {
  const { hasPermission } = usePermissions();
  return (
    <div style={{ marginBottom: '0.625rem' }}>
      <div style={{
        fontSize: '0.625rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: isDark ? '#6b7280' : '#9ca3af',
        marginBottom: '0.375rem',
      }}>
        {title} ({entries.length})
      </div>
      {entries.map((entry) => {
        const isOwn = entry.added_by_member_id === memberId;
        const canDelete = isOwn || hasPermission('DELETE_METADATA');

        return (
          <div
            key={entry.meta_id}
            style={{
              padding: '0.5rem 0.625rem',
              borderRadius: '0.5rem',
              marginBottom: '0.375rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
              background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
            }}
          >
            {/* Entry header: who added + when */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.25rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <span style={{
                  fontSize: '0.5625rem',
                  fontWeight: 500,
                  color: isDark ? '#9ca3af' : '#6b7280',
                }}>
                  {entry.added_by_name}
                </span>
                <span style={{
                  fontSize: '0.5rem',
                  padding: '0.05rem 0.3rem',
                  borderRadius: '9999px',
                  background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  color: isDark ? '#6b7280' : '#9ca3af',
                  fontWeight: 500,
                }}>
                  {entry.added_by_role}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <span style={{
                  fontSize: '0.5625rem',
                  color: isDark ? '#4b5563' : '#d1d5db',
                }}>
                  {formatTimestamp(entry.created_at)}
                </span>
                {canDelete && onDelete && (
                  <button
                    onClick={() => onDelete(entry)}
                    style={{
                      background: isDark ? 'rgba(239,68,68,0.14)' : '#fef2f2',
                      border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.3)'}`,
                      cursor: 'pointer',
                      fontSize: '0.625rem',
                      color: '#ef4444',
                      padding: '0.125rem 0.3rem',
                      borderRadius: '0.25rem',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = isDark ? 'rgba(239,68,68,0.24)' : '#fee2e2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = isDark ? 'rgba(239,68,68,0.14)' : '#fef2f2'}
                    title="Delete"
                    aria-label="Delete entry"
                  >
                    🗑
                  </button>
                )}
              </div>
            </div>
            {/* Content */}
            {renderContent(entry)}
          </div>
        );
      })}
    </div>
  );
}


/**
 * Format ISO timestamp to IST time string.
 */
function formatTimestamp(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d)) return '';
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return '';
  }
}


// ═══════════════════════════════════════════════════════════════════════
// PrescriptionSubPanel — Add (digital) + Upload sub-tabs for prescription
// ═══════════════════════════════════════════════════════════════════════

function PrescriptionSubPanel({
  visit, isDark, prescriptionEntries, handleSuccess, handleDelete,
  handleViewFile, memberId, role, setActiveAction,
  doctorSpecialization, isDoctor, loadingMeta, readOnly = false,
}) {
  const { recordPrescriptionTabUsage } = useWorkflowStore();
  const { hasPermission } = usePermissions();
  // Default to digital prescription form; upload is secondary
  const [showUpload, setShowUpload] = useState(false);

  // PDF generation state
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Read-only mode for COMPLETED visits (toggle to edit creates versioned entry)
  const [editingCompletedRx, setEditingCompletedRx] = useState(false);
  // The just-generated prescription's data — passed back as initialData so the
  // form re-syncs (incl. outside investigations) if it remounts after a generate,
  // instead of re-initialising to an empty []. State-retention only; the existing
  // delete/replace logic in handleSave is unchanged.
  const [currentRxData, setCurrentRxData] = useState(null);
  // Tracks meta_id of the prescription saved in the current session so re-saving
  // replaces it instead of accumulating a second entry (same pattern as DoctorQueueView).
  const [currentRxMetaId, setCurrentRxMetaId] = useState(null);
  const [rxSaveCount, setRxSaveCount] = useState(0);

  // Find the latest active digital prescription for read-only display
  const latestDigitalRx = pickLatestDigitalRx(prescriptionEntries);

  const isCompletedWithRx = visit.status === 'COMPLETED' && latestDigitalRx;

  // Auto-resume: a refresh resets currentRxMetaId/currentRxData (session-only
  // React state), which previously meant the form silently forgot which
  // prescription it was continuing -- attached images (which can't live in
  // the local draft-autosave the way text fields do) simply vanished from
  // view, and the next save created a SEPARATE new prescription instead of
  // continuing/replacing the one with images. Seed both from the visit's
  // latest not-yet-completed Rx once metadata has loaded.
  //
  // Also re-syncs (not just first-load): right after add_prescription's
  // own onSuccess fires, currentRxData is set from the SAVE response,
  // which has no attached_images field at all (only the child
  // PRESCRIPTION_IMAGE rows carry that) -- so immediately post-save,
  // currentRxData.attached_images goes stale/missing even though the
  // backend has already correctly relinked/added images. handleSuccess's
  // refreshMetadata() re-fetches shortly after, and once THAT lands (same
  // meta_id, but now with the fresh attached_images grouped on), this
  // effect re-fires to fill the gap -- found live: a doctor who couldn't
  // see their already-attached images this way re-attached the same
  // photos a second time. Only acts when currentRxMetaId is unset (first
  // load) or already matches this entry (fresher data for the SAME
  // prescription) -- never overwrites state for a save still in flight.
  const latestRxImageIds = (latestDigitalRx?.attached_images || []).map(img => img.meta_id).join(',');
  useEffect(() => {
    if (isCompletedWithRx || !latestDigitalRx) return;
    if (!currentRxMetaId || currentRxMetaId === latestDigitalRx.meta_id) {
      setCurrentRxMetaId(latestDigitalRx.meta_id);
      setCurrentRxData(latestDigitalRx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestDigitalRx?.meta_id, latestRxImageIds, isCompletedWithRx, currentRxMetaId, rxSaveCount]);

  // Delete confirmation state for existing prescriptions
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleGeneratePdf = useCallback(async () => {
    setPdfGenerating(true);
    setPdfError(null);
    try {
      await openPrescriptionPdf(visit.visit_id);
    } catch (err) {
      setPdfError(err.message || 'Failed to generate PDF');
      setTimeout(() => setPdfError(null), 5000);
    } finally {
      setPdfGenerating(false);
    }
  }, [visit.visit_id]);

  // Wrap onSuccess to also record which tab was used
  const handlePrescriptionSuccess = useCallback(() => {
    handleSuccess();
    recordPrescriptionTabUsage('add');
  }, [handleSuccess, recordPrescriptionTabUsage]);

  // The pad's onSuccess. Bumps rxSaveCount so the resume effect above re-runs
  // even when the saved Rx id is unchanged (first Generate adopts rx-1 from a
  // WS echo, then onSuccess overwrites the data without images -> D1r). A failed
  // image upload on a COMPLETED-visit edit keeps the editor open (error + staged
  // file stay visible for retry, D2c).
  const handlePadSuccess = useCallback((result) => {
    if (result?.metaId) setCurrentRxMetaId(result.metaId);
    if (result?.data) setCurrentRxData(result.data);
    setRxSaveCount((c) => c + 1);
    handlePrescriptionSuccess();
    if (!result?.imageUploadFailed) setEditingCompletedRx(false);
  }, [handlePrescriptionSuccess]);

  // Delete with confirmation
  const handleDeleteWithConfirm = useCallback((entry) => {
    if (confirmDeleteId === entry.meta_id) {
      handleDelete(entry);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(entry.meta_id);
      setTimeout(() => setConfirmDeleteId((current) => current === entry.meta_id ? null : current), 4000);
    }
  }, [confirmDeleteId, handleDelete]);

  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textMuted = isDark ? '#6b7280' : '#9ca3af';

  return (
    <div style={{
      margin: '0 1rem 0.75rem',
      borderRadius: '0.625rem',
      border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.1)'}`,
      background: isDark ? 'rgba(13, 148, 136,0.03)' : 'rgba(13, 148, 136,0.015)',
      overflow: 'hidden',
    }}>
      {/* Digital Prescription Form — always shown by default */}
      <div style={{ padding: '0.625rem' }}>
        {/* COMPLETED visit + saved digital Rx → read-only view (unless toggled to edit) */}
        {isCompletedWithRx && !editingCompletedRx ? (
          <PrescriptionReadOnlyView
            prescriptionEntry={latestDigitalRx}
            visitId={visit.visit_id}
            // Permission AND printability (owner D28): the card is shown on
            // the four-term content rule, but the server's per-entry
            // `printable` says whether THIS entry produces a document, so a
            // legacy diagnosis-only Rx gets the card without a button that
            // would 400. This card shows exactly ONE entry, so per-entry is
            // the matching granularity. `=== true`, never `!== false`:
            // `printable` is absent on legacy rows.
            canGeneratePdf={hasPermission('GENERATE_PRESCRIPTION_PDF') && latestDigitalRx.printable === true}
            onEdit={() => setEditingCompletedRx(true)}
            onGeneratePdf={handleGeneratePdf}
            pdfGenerating={pdfGenerating}
          />
        ) : readOnly ? (
          // RV-3 — the one card R5 had left ungated: no digital-form/upload
          // write surface under readOnly, no edit toggle (onEdit withheld,
          // same "withhold the control" shape as `deleteHandler`). Existing
          // entries still show via the "Saved Prescriptions" list below.
          latestDigitalRx ? (
            <PrescriptionReadOnlyView
              prescriptionEntry={latestDigitalRx}
              visitId={visit.visit_id}
              onGeneratePdf={handleGeneratePdf}
              pdfGenerating={pdfGenerating}
            />
          ) : null
        ) : isDoctor ? (
          <DigitalPrescriptionForm
            visit={visit}
            visitId={visit.visit_id}
            onSuccess={handlePadSuccess}
            doctorSpecialization={doctorSpecialization}
            patientId={visit.patient_id}
            visitStatus={visit.status}
            editMetaId={editingCompletedRx ? latestDigitalRx?.meta_id : (currentRxMetaId || undefined)}
            initialData={editingCompletedRx ? latestDigitalRx : (currentRxData || undefined)}
            metadataLoaded={!loadingMeta}
          />
        ) : !showUpload ? (
          <>
            <DigitalPrescriptionForm
              visit={visit}
              visitId={visit.visit_id}
              onSuccess={handlePadSuccess}
              doctorSpecialization={doctorSpecialization}
              patientId={visit.patient_id}
              visitStatus={visit.status}
              editMetaId={editingCompletedRx ? latestDigitalRx?.meta_id : (currentRxMetaId || undefined)}
              initialData={editingCompletedRx ? latestDigitalRx : (currentRxData || undefined)}
              metadataLoaded={!loadingMeta}
            />
            {/* Small "upload" link below — non-doctor only */}
            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              <button
                onClick={() => setShowUpload(true)}
                style={{
                  background: isDark ? '#26262e' : '#f8fafc',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                  borderRadius: '0.375rem', padding: '0.15rem 0.5rem', cursor: 'pointer',
                  fontSize: '0.5625rem', color: textMuted,
                  textDecoration: 'underline',
                }}
              >
                📤 Or upload a prescription photo/file
              </button>
            </div>
          </>
        ) : (
          <>
            <AddPrescription
              visitId={visit.visit_id}
              metaType="PRESCRIPTION"
              onSuccess={() => { handleSuccess(); recordPrescriptionTabUsage('upload'); }}
              onClose={() => setShowUpload(false)}
            />
            <div style={{ textAlign: 'center', marginTop: '0.375rem' }}>
              <button
                onClick={() => setShowUpload(false)}
                style={{
                  background: isDark ? 'rgba(13,148,136,0.14)' : '#f0fdfa',
                  border: `1px solid ${isDark ? 'rgba(45,212,191,0.35)' : 'rgba(94,234,212,0.6)'}`,
                  borderRadius: '0.375rem', padding: '0.15rem 0.5rem', cursor: 'pointer',
                  fontSize: '0.5625rem', color: isDark ? '#5eead4' : '#0d9488',
                  textDecoration: 'underline',
                }}
              >
                💊 Back to digital prescription
              </button>
            </div>
          </>
        )}
      </div>

      {/* Existing prescriptions — clean list with confirmation-based delete */}
      {/* Hide when read-only view is showing (it already renders the RX) */}
      {!isDoctor && !(isCompletedWithRx && !editingCompletedRx) && prescriptionEntries.length > 0 && (
        <div style={{
          padding: '0.5rem 0.625rem 0.625rem',
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
        }}>
          <div style={{
            fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: textMuted, marginBottom: '0.375rem',
          }}>
            💊 Saved Prescriptions ({prescriptionEntries.length})
          </div>

          {prescriptionEntries.map((entry) => (
            <div
              key={entry.meta_id}
              style={{
                padding: '0.5rem 0.625rem',
                borderRadius: '0.5rem',
                marginBottom: '0.375rem',
                border: `1px solid ${confirmDeleteId === entry.meta_id
                  ? 'rgba(239,68,68,0.3)'
                  : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')}`,
                background: confirmDeleteId === entry.meta_id
                  ? (isDark ? 'rgba(239,68,68,0.04)' : 'rgba(239,68,68,0.02)')
                  : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
                transition: 'all 0.15s',
              }}
            >
              {/* Timestamp + who added + approval badge (Phase 1b) */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '0.25rem', gap: '0.375rem',
              }}>
                <span style={{ fontSize: '0.5625rem', color: textMuted }}>
                  {entry.added_by_name} · {formatTimestamp(entry.created_at)}
                </span>
                {entry.approval_status === 'pending_approval' && (
                  <span style={{
                    fontSize: '0.5rem', fontWeight: 700,
                    padding: '0.05rem 0.3rem', borderRadius: '9999px',
                    background: isDark ? 'rgba(234,179,8,0.15)' : 'rgba(234,179,8,0.1)',
                    color: isDark ? '#fbbf24' : '#a16207',
                    border: `1px solid ${isDark ? 'rgba(234,179,8,0.3)' : 'rgba(234,179,8,0.25)'}`,
                  }}>
                    PENDING APPROVAL
                  </span>
                )}
                {entry.approval_status === 'rejected' && (
                  <span style={{
                    fontSize: '0.5rem', fontWeight: 700,
                    padding: '0.05rem 0.3rem', borderRadius: '9999px',
                    background: isDark ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.08)',
                    color: isDark ? '#fca5a5' : '#dc2626',
                    border: `1px solid ${isDark ? 'rgba(220,38,38,0.3)' : 'rgba(220,38,38,0.25)'}`,
                  }} title={entry.rejection_reason || 'Rejected by senior'}>
                    REJECTED
                  </span>
                )}
                {entry.approval_status === 'approved' && entry.approved_by_member_id && (
                  <span style={{
                    fontSize: '0.5rem', fontWeight: 700,
                    padding: '0.05rem 0.3rem', borderRadius: '9999px',
                    background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)',
                    color: isDark ? '#6ee7b7' : '#059669',
                    border: `1px solid ${isDark ? 'rgba(16,185,129,0.3)' : 'rgba(16,185,129,0.25)'}`,
                  }}>
                    APPROVED
                  </span>
                )}
              </div>

              {/* Content */}
              {entry.prescription_type === 'digital' && hasPrescriptionContent(entry) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                  {(entry.medicines || []).map((med, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap',
                      fontSize: '0.6875rem', color: isDark ? '#e8e9ed' : '#374151',
                    }}>
                      <span style={{ fontWeight: 600 }}>{med.medicine}</span>
                      {med.strength && <span style={{ color: isDark ? '#2dd4bf' : '#0f766e', fontSize: '0.625rem' }}>{med.strength}</span>}
                      {med.form && <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.625rem' }}>({med.form})</span>}
                      {med.dosage_schedule && <span style={{ fontSize: '0.625rem', color: isDark ? '#60a5fa' : '#3b82f6' }}>
                        {formatDoseSchedule(med.dosage_schedule)}
                      </span>}
                      {med.timing && <span style={{ fontSize: '0.5625rem', color: textMuted }}>{med.timing}</span>}
                      {med.duration && <span style={{ fontSize: '0.5625rem', color: textMuted }}>{med.duration}</span>}
                    </div>
                  ))}
                  {entry.investigations && entry.investigations.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center', marginTop: '0.125rem' }}>
                      <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: isDark ? '#fbbf24' : '#d97706' }}>🧪</span>
                      {entry.investigations.map((inv, i) => (
                        <span key={i} style={{
                          fontSize: '0.5625rem', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem',
                          background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)',
                          color: isDark ? '#fbbf24' : '#d97706', fontWeight: 500,
                        }}>{inv}</span>
                      ))}
                    </div>
                  )}
                  {entry.doctor_notes && (
                    <p style={{ margin: '0.125rem 0 0', fontSize: '0.625rem', fontStyle: 'italic', color: textMuted }}>
                      📝 {entry.doctor_notes}
                    </p>
                  )}
                </div>
              ) : (
                <FileEntryContent entry={entry} isDark={isDark} onView={handleViewFile} />
              )}

              {/* Single delete button with confirmation — always at the bottom */}
              {!readOnly && visit.status !== 'COMPLETED' && (
                <button
                  onClick={() => handleDeleteWithConfirm(entry)}
                  style={{
                    marginTop: '0.375rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '0.375rem',
                    border: `1px solid ${confirmDeleteId === entry.meta_id
                      ? 'rgba(239,68,68,0.5)'
                      : (isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.15)')}`,
                    background: confirmDeleteId === entry.meta_id
                      ? (isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.1)')
                      : 'transparent',
                    color: isDark ? '#fca5a5' : '#dc2626',
                    fontSize: '0.5625rem',
                    fontWeight: confirmDeleteId === entry.meta_id ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {confirmDeleteId === entry.meta_id ? '🗑 Tap again to delete' : '🗑 Delete'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* View prescription — only when NOT showing the read-only view (which has its own button).
          Acts on an ALREADY-SAVED Rx, so the verb is "View", not "Generate" (owner, this task);
          the prescription pad's own save button keeps "Generate Prescription (…)". */}
      {/* PRINTABILITY, not content (owner D28): the server's per-entry
          `printable` flag, never a copy of its rule. `some(... === true)` —
          `printable` is absent on legacy rows and null off PRESCRIPTION rows,
          so `!== false` would offer the button on every one of them.
          This button prints the VISIT, and no per-visit flag exists (parked,
          bk-1179) — an OR over the visit's entries is the closest available
          answer and is exactly what this surface already did. */}
      {hasPermission('GENERATE_PRESCRIPTION_PDF') && !(isCompletedWithRx && !editingCompletedRx) && prescriptionEntries.some(e => e.prescription_type === 'digital' && e.printable === true) && (() => {
        // Phase 1b — block generation while any Rx is pending senior approval.
        const pendingEntries = prescriptionEntries.filter(
          (e) => e.approval_status === 'pending_approval',
        );
        const rejectedEntries = prescriptionEntries.filter(
          (e) => e.approval_status === 'rejected',
        );
        const pdfBlocked = pendingEntries.length > 0;
        return (
          <div style={{
            padding: '0.5rem 0.625rem 0.625rem',
            borderTop: `1px solid ${borderColor}`,
          }}>
            {pdfBlocked && (
              <div style={{
                marginBottom: '0.375rem',
                padding: '0.375rem 0.5rem',
                borderRadius: '0.375rem',
                background: isDark ? 'rgba(234,179,8,0.1)' : 'rgba(234,179,8,0.08)',
                border: `1px solid ${isDark ? 'rgba(234,179,8,0.3)' : 'rgba(234,179,8,0.25)'}`,
                fontSize: '0.6875rem',
                color: isDark ? '#fbbf24' : '#a16207',
                textAlign: 'center',
              }}>
                ⏳ Awaiting senior doctor's approval — PDF unlocks after sign-off.
              </div>
            )}
            {rejectedEntries.length > 0 && (
              <div style={{
                marginBottom: '0.375rem',
                padding: '0.375rem 0.5rem',
                borderRadius: '0.375rem',
                background: isDark ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.04)',
                border: `1px solid ${isDark ? 'rgba(220,38,38,0.3)' : 'rgba(220,38,38,0.2)'}`,
                fontSize: '0.6875rem',
                color: isDark ? '#fca5a5' : '#dc2626',
                textAlign: 'left',
              }}>
                ❌ Rx rejected by senior.
                {rejectedEntries[0].rejection_reason
                  ? ` Reason: ${rejectedEntries[0].rejection_reason}`
                  : ''}
                {' '}Edit and resubmit with your next Complete.
              </div>
            )}
            <button
              onClick={handleGeneratePdf}
              disabled={pdfGenerating || pdfBlocked}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: `1px solid ${isDark ? 'rgba(13,148,136,0.3)' : 'rgba(13,148,136,0.25)'}`,
                background: isDark ? 'rgba(13,148,136,0.08)' : 'rgba(13,148,136,0.04)',
                color: isDark ? '#5eead4' : '#0d9488',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: (pdfGenerating || pdfBlocked) ? 'not-allowed' : 'pointer',
                opacity: (pdfGenerating || pdfBlocked) ? 0.4 : 1,
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              {pdfGenerating ? '⏳ Preparing…' : '📄 View prescription'}
            </button>
            {pdfError && (
              <div style={{
                marginTop: '0.375rem',
                padding: '0.375rem 0.5rem',
                borderRadius: '0.375rem',
                background: isDark ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.04)',
                fontSize: '0.625rem',
                color: isDark ? '#fca5a5' : '#dc2626',
                textAlign: 'center',
              }}>
                ⚠️ {pdfError}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

